import type { GoodsReceiptTotals } from "../../types"
import { calculatePurchaseOrderEstimatedTotals, getOrderShipToLocation, hasGoodsReceiptLineValues } from "../../utils"

import type {
  GoodsReceiptFormValues,
  IncomingPurchaseOrderLineFormValues,
  IncomingPurchaseOrdersFormValues,
} from "./schemas"

import type {
  GoodsReceipt,
  GoodsReceiptProductDetailsBatchUpdateInput,
  GoodsReceiptUpdateInput,
  Order,
  WarehouseAreaOutputList,
} from "@/graphql/codegen/graphql"
import { getLocationWarehousesPrefix } from "@/screens/Procurement/PurchaseOrder/utils/common"
import type { AutocompleteOption } from "@/types/form-fields"
import { roundToPrecision } from "@/utils/number"
import { formatWarehouseLabel } from "@/utils/warehouse"

export const cleanPurchaseOrderSearchValue = (value: string): string => {
  return value.startsWith("PO# ") ? value.replace("PO# ", "") : value
}

export const getPurchaseOrderSearchInitialValue = (searchValue: string | AutocompleteOption): string => {
  if (typeof searchValue === "string") {
    return cleanPurchaseOrderSearchValue(searchValue)
  }
  if (searchValue?.id !== null) {
    return String(searchValue.id)
  }
  return cleanPurchaseOrderSearchValue(searchValue?.label ?? "")
}

export const getWarehouseAutocompleteOptions = (
  warehouses: WarehouseAreaOutputList[]
): AutocompleteOption<string>[] => {
  return warehouses
    .map((warehouse) => ({
      id: warehouse.warehouseAreaId,
      label: `${warehouse.externalSystemId} ${warehouse.location.description}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getGoodsReceiptDefaultFormValues = (
  goodsReceipt: GoodsReceipt | null,
  selectedPurchaseOrders: Order[] = []
): GoodsReceiptFormValues => {
  let deliveryLocation = { id: "", label: "" }

  if (goodsReceipt) {
    // If editing existing goods receipt, use its warehouse area
    deliveryLocation = {
      id: goodsReceipt.warehouseArea!.warehouseAreaId,
      label: formatWarehouseLabel(goodsReceipt.warehouseArea!),
    }
  } else if (selectedPurchaseOrders.length > 0) {
    const firstOrder = selectedPurchaseOrders[0]
    const shipToLocation = getOrderShipToLocation(firstOrder)

    if (shipToLocation && firstOrder.warehouse) {
      deliveryLocation = {
        id: firstOrder.warehouse.warehouseAreaId,
        label: `${firstOrder.warehouse.externalSystemId || getLocationWarehousesPrefix(shipToLocation)} ${shipToLocation.description}`,
      }
    }
  }

  return {
    trackingNumbers: goodsReceipt?.trackingNumbers ?? [""],
    estimatedDeliveryOn: goodsReceipt?.estimatedDeliveryOn ?? null,
    deliveryLocation,
    estimatedPallets: goodsReceipt?.estimatedPalletsCount ?? undefined,
    supplier: goodsReceipt
      ? {
          id: goodsReceipt.supplier?.companyId ?? "",
          label: goodsReceipt.supplier?.name ?? "",
        }
      : undefined,
  }
}

export const serializeGoodsReceiptForm = (
  formValues: GoodsReceiptFormValues,
  orderIds: number[]
): Omit<GoodsReceiptUpdateInput, "goodsReceiptId"> => {
  let supplierId: string | null | undefined = formValues.supplier?.id

  if (typeof supplierId === "string" && supplierId.trim() === "") {
    supplierId = null
  }

  return {
    orderIds,
    trackingNumbers: formValues.trackingNumbers?.filter((tn) => tn.trim() !== ""),
    estimatedDeliveryOn: formValues.estimatedDeliveryOn,
    warehouseId: formValues.deliveryLocation.id,
    estimatedPalletsCount: formValues.estimatedPallets,
    supplierId,
  }
}

export const getIncomingPurchaseOrdersFormDefaultValues = (
  goodsReceipt: GoodsReceipt
): IncomingPurchaseOrdersFormValues => ({
  goodsReceiptId: goodsReceipt.goodsReceiptId,
  purchaseOrders: (goodsReceipt.orders ?? []).map((order) => ({
    purchaseOrderId: order.orderId,
    lines: (order.orderLines ?? []).map((purchaseOrderLine) => {
      const product = purchaseOrderLine.product!
      const existingGoodsReceiptLine = goodsReceipt.productDetails?.find(
        (goodsReceiptLine) => goodsReceiptLine.orderLineId === purchaseOrderLine.orderLineId
      )

      if (existingGoodsReceiptLine && hasGoodsReceiptLineValues(existingGoodsReceiptLine)) {
        return {
          purchaseOrderLineId: purchaseOrderLine.orderLineId,
          palletsCount: existingGoodsReceiptLine.estimatedPallets ?? 0,
          unitsCount: existingGoodsReceiptLine.estimatedUnits ?? 0,
          quantity: existingGoodsReceiptLine.estimatedTotalUom ?? 0,
          uomId: product.packageUsWeightUom!,
        }
      }

      const purchaseOrderTotals = calculatePurchaseOrderEstimatedTotals([purchaseOrderLine])

      return {
        purchaseOrderLineId: purchaseOrderLine.orderLineId,
        palletsCount: purchaseOrderTotals.palletsCount,
        unitsCount: purchaseOrderTotals.unitsCount,
        quantity: purchaseOrderTotals.quantity,
        uomId: purchaseOrderTotals.uomId,
      }
    }),
  })),
})

export const calculateIncomingPurchaseOrderFormTotals = (
  goodsReceiptLines: IncomingPurchaseOrderLineFormValues[]
): GoodsReceiptTotals => {
  const uniqueUomIds = new Set<string>()

  const totals = goodsReceiptLines.reduce(
    (acc, line) => {
      uniqueUomIds.add(line.uomId)

      return {
        palletsCount: acc.palletsCount + (line.palletsCount ?? 0),
        unitsCount: acc.unitsCount + (line.unitsCount ?? 0),
        quantity: acc.quantity + (line.quantity ?? 0),
        uomId: line.uomId,
      }
    },
    { palletsCount: 0, unitsCount: 0, quantity: 0, uomId: "" }
  )

  const hasSameUom = uniqueUomIds.size <= 1

  return {
    palletsCount: roundToPrecision(totals.palletsCount, 2),
    unitsCount: roundToPrecision(totals.unitsCount, 2),
    quantity: hasSameUom ? roundToPrecision(totals.quantity, 2) : null,
    uomId: hasSameUom ? totals.uomId : "",
  }
}

export const serializeIncomingPurchaseOrdersForm = (
  formValues: IncomingPurchaseOrdersFormValues
): GoodsReceiptProductDetailsBatchUpdateInput => {
  return {
    goodsReceiptId: formValues.goodsReceiptId,
    productDetails: formValues.purchaseOrders.flatMap((purchaseOrder) =>
      purchaseOrder.lines.map((purchaseOrderLine) => ({
        orderLineId: purchaseOrderLine.purchaseOrderLineId,
        estimatedPallets: purchaseOrderLine.palletsCount,
        estimatedUnits: purchaseOrderLine.unitsCount,
        estimatedTotalUom: purchaseOrderLine.quantity,
      }))
    ),
  }
}

export const canSoftReceive = (formValues: IncomingPurchaseOrdersFormValues): boolean => {
  return formValues.purchaseOrders.every((purchaseOrder) =>
    purchaseOrder.lines.every(({ palletsCount, unitsCount, quantity }) =>
      // eslint-disable-next-line eqeqeq
      [palletsCount, unitsCount, quantity].every((value) => value != null)
    )
  )
}
